import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMoney(amount, currency) {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(0)}`;
  }
}

/**
 * Buy Now panel — marketing-site styled package selector.
 * Reads is_active=true rows from /api/public/stripe-packages and POSTs to
 * /api/checkout/session to create a Stripe Checkout Session, then redirects.
 */
export default function BuyNowPanel() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/stripe-packages`)
      .then(r => { if (alive) setPackages(r.data || []); })
      .catch(() => { if (alive) setPackages([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleBuy = async (price_id) => {
    setPurchasingId(price_id);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        price_id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      toast.error('Could not start checkout. Please try again shortly.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Could not start checkout. Please try again.');
    } finally {
      setPurchasingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="buy-now-loading">
        <Loader2 size={22} className="text-[#A89F91] animate-spin" />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="border border-[#E7E5DF] bg-white p-10 text-center" data-testid="buy-now-empty">
        <p className="font-['Cormorant_Garamond'] text-xl text-[#1C1917] mb-2">Packages coming soon</p>
        <p className="text-sm text-[#78716C] max-w-md mx-auto leading-relaxed">
          Online purchase is briefly unavailable. Please use the Inquire tab and our team will get back to you within one business day.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="buy-now-panel">
      {/* No-refund policy line */}
      <div className="mb-10 sm:mb-12 max-w-2xl mx-auto" data-testid="buy-now-policy">
        <div className="flex items-start gap-3 border border-[#E7E5DF] bg-[#FDFCFA] px-5 py-4">
          <ShieldCheck size={18} className="text-[#A89F91] mt-0.5 shrink-0" />
          <p className="text-sm text-[#57534E] leading-relaxed">
            All purchases are final. No refunds. Package may be transferred to another family member if circumstances require.
          </p>
        </div>
      </div>

      {/* Package cards */}
      <div className="space-y-5 max-w-2xl mx-auto">
        {packages.map((p) => (
          <article
            key={p.id}
            data-testid={`buy-now-card-${p.stripe_price_id}`}
            className="border border-[#E7E5DF] bg-white hover:border-[#A89F91] transition-colors duration-300"
          >
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-4 mb-3">
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] leading-tight">
                  {p.package_name}
                </h3>
                <div className="text-right">
                  <p className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-none tabular-nums">
                    {fmtMoney(p.amount, p.currency)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-[#A89F91] mt-1.5">
                    {(p.currency || 'usd').toUpperCase()} · One-time
                  </p>
                </div>
              </div>
              {p.description && (
                <p className="text-base text-[#57534E] leading-relaxed mb-7 max-w-xl">{p.description}</p>
              )}
              <button
                onClick={() => handleBuy(p.stripe_price_id)}
                disabled={purchasingId === p.stripe_price_id || !p.amount}
                data-testid={`buy-now-button-${p.stripe_price_id}`}
                className="inline-flex items-center justify-center gap-2 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-50 disabled:cursor-not-allowed px-10 py-3.5 text-sm font-medium tracking-wide transition-all duration-300"
              >
                {purchasingId === p.stripe_price_id ? (
                  <><Loader2 size={15} className="animate-spin" /> Redirecting to checkout…</>
                ) : (
                  <>Buy Now <ArrowRight size={14} /></>
                )}
              </button>
            </div>
          </article>
        ))}
      </div>

      <p className="text-center text-xs text-[#A89F91] mt-10 max-w-md mx-auto leading-relaxed">
        You&apos;ll be redirected to Stripe to complete your purchase securely. Pay with card or Affirm.
      </p>
    </div>
  );
}
